/**
 * Phase 7.1 (continued) — per-panel error boundary.
 *
 * Wraps an individual case-workspace panel (chat, editor, context
 * pane, harnesses) so a render error inside one panel doesn't kill
 * the whole route via `RouteErrorBoundary`. The user can keep
 * working in the surviving panels and click "Tentar novamente" to
 * remount just the failed subtree.
 *
 * Differences from `RouteErrorBoundary` (which is route-level):
 *  - Smaller inline fallback that fits inside a panel — no full-
 *    screen overlay, no reload-the-whole-page CTA.
 *  - Manual retry button that resets `state.error` to `null`. If
 *    the underlying issue is transient, the panel recovers; if
 *    it's persistent, the panel crashes again and the user can
 *    fall back to a full reload.
 *  - No automatic reset on navigation. A crashed panel should stay
 *    crashed until the user explicitly retries — silently re-
 *    rendering on every URL change would mask repeated failures.
 *  - No chunk-load detection. Chunks resolve at route lazy-load;
 *    by the time a panel renders, its chunk is loaded.
 *
 * Sits inside `RouteErrorBoundary` (App.tsx) so any error not
 * caught here still bubbles to the route-level fallback. Sentry is
 * notified at the panel level with a `panel_label` tag so dashboards
 * can slice failures by surface.
 */

import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface State {
  error: Error | null;
}

interface Props {
  /** Human-readable name shown in the fallback ("Chat", "Editor",
   *  etc.). Also tagged on the Sentry event for filterability. */
  label: string;
  children: ReactNode;
}

export class PanelErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        source: "panel-error-boundary",
        panel_label: this.props.label,
      },
      extra: { componentStack: info.componentStack },
    });
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        data-testid="panel-error-fallback"
        data-panel-label={this.props.label}
        className="flex h-full flex-1 flex-col items-center justify-center gap-2 p-4 text-center"
      >
        <AlertTriangle size={20} className="text-amber-500" />
        <p className="text-xs text-slate-600 dark:text-slate-300">
          Erro no painel <span className="font-medium">{this.props.label}</span>.
        </p>
        <button
          type="button"
          onClick={this.handleRetry}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <RefreshCw size={12} />
          Tentar novamente
        </button>
      </div>
    );
  }
}
